import { getAnalyticsEvents } from './analyticsService';
import { getCollection } from './firestoreService';
import { convertTimestamp } from './firestoreService';
import { differenceInDays, differenceInMinutes } from 'date-fns';

export interface TopicCompletionMetrics {
  topicTitle: string;
  category: string;
  completionRate: number;
  averageTimeToComplete: number;
  totalAttempts: number;
  completions: number;
}

export interface DropOffPoint {
  moduleIndex: number;
  moduleName: string;
  dropOffCount: number;
  dropOffRate: number;
}

export interface ProgressVelocity {
  userId: string;
  topicTitle: string;
  progressIncrease: number;
  daysTracked: number;
  velocity: number; // progress per day
}

export interface PerformanceOverTime {
  week: string;
  average_accuracy: number;
  total_items: number;
  completed_items: number;
  completion_rate: number;
}

export interface WeakArea {
  topic: string;
  modules_count: number;
  exercises_count: number;
  quizzes_count: number;
  overall_accuracy: number;
  needs_review: boolean;
}

export interface PracticePlanSuggestion {
  topic: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  recommended_actions: string[];
  estimated_time_minutes: number;
}

export interface LearningProgressAnalytics {
  topicCompletionMetrics: TopicCompletionMetrics[];
  dropOffPoints: DropOffPoint[];
  stuckUsers: Array<{ userId: string; topicTitle: string; lastProgress: number; daysSinceUpdate: number }>;
  quizSuccessRates: Map<string, number>;
  exerciseSuccessRates: Map<string, number>;
  progressVelocity: ProgressVelocity[];
  // NEW: Comprehensive learning path analytics
  modulesPerformanceOverTime: PerformanceOverTime[];
  exercisesPerformanceOverTime: PerformanceOverTime[];
  quizzesPerformanceOverTime: PerformanceOverTime[];
  weakAreas: WeakArea[];
  practicePlanSuggestions: PracticePlanSuggestion[];
  overallAccuracy: number;
  completionRate: number;
}

export const getLearningProgressAnalytics = async (
  days: number = 30
): Promise<LearningProgressAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days * 2);
    
    // Get progress events
    const progressEvents = events.filter(
      (e) => e.eventName === 'learning_progress_updated' || e.eventName.includes('progress')
    );

    // Get completion events
    const completionEvents = events.filter(
      (e) => e.eventName === 'topic_completed' || e.eventName === 'learning_path_completed'
    );

    // Get quiz/exercise events
    const quizEvents = events.filter((e) => e.eventName.includes('quiz'));
    const exerciseEvents = events.filter((e) => e.eventName.includes('exercise'));

    // Calculate topic completion metrics
    const topicMetricsMap = new Map<string, {
      category: string;
      completions: number;
      attempts: number;
      completionTimes: number[];
      totalProgress: number;
      progressCount: number;
    }>();

    progressEvents.forEach((event) => {
      const topicTitle = event.properties.topic_title as string;
      const category = (event.properties.category as string) || 'Other';
      const completionPercentage = (event.properties.completion_percentage as number) || 0;
      const isCompleted = event.properties.is_completed as boolean || false;

      if (topicTitle) {
        if (!topicMetricsMap.has(topicTitle)) {
          topicMetricsMap.set(topicTitle, {
            category,
            completions: 0,
            attempts: 0,
            completionTimes: [],
            totalProgress: 0,
            progressCount: 0,
          });
        }

        const metrics = topicMetricsMap.get(topicTitle)!;
        metrics.attempts++;
        metrics.totalProgress += completionPercentage;
        metrics.progressCount++;

        if (isCompleted) {
          metrics.completions++;
          // Would track completion time if available
        }
      }
    });

    // Calculate completion times from completion events
    completionEvents.forEach((event) => {
      const topicTitle = event.properties.topic_title as string;
      const timeSpent = (event.properties.time_spent_minutes as number) || 0;
      
      if (topicTitle && topicMetricsMap.has(topicTitle) && timeSpent > 0) {
        topicMetricsMap.get(topicTitle)!.completionTimes.push(timeSpent);
      }
    });

    const topicCompletionMetrics: TopicCompletionMetrics[] = Array.from(topicMetricsMap.entries())
      .map(([topicTitle, metrics]) => ({
        topicTitle,
        category: metrics.category,
        completionRate: metrics.attempts > 0 ? (metrics.completions / metrics.attempts) * 100 : 0,
        averageTimeToComplete: metrics.completionTimes.length > 0
          ? metrics.completionTimes.reduce((a, b) => a + b, 0) / metrics.completionTimes.length
          : 0,
        totalAttempts: metrics.attempts,
        completions: metrics.completions,
      }))
      .sort((a, b) => b.completionRate - a.completionRate);

    // Drop-off points (simplified - would need module-level tracking)
    const dropOffPoints: DropOffPoint[] = [];

    // Quiz success rates
    const quizSuccessMap = new Map<string, { total: number; passed: number }>();
    quizEvents.forEach((event) => {
      const topicTitle = (event.properties.topic_title as string) || 'Unknown';
      const score = (event.properties.score as number) || 0;
      const passed = score >= 0.7; // 70% threshold

      if (!quizSuccessMap.has(topicTitle)) {
        quizSuccessMap.set(topicTitle, { total: 0, passed: 0 });
      }
      const metrics = quizSuccessMap.get(topicTitle)!;
      metrics.total++;
      if (passed) metrics.passed++;
    });

    const quizSuccessRates = new Map<string, number>();
    quizSuccessMap.forEach((metrics, topic) => {
      quizSuccessRates.set(topic, metrics.total > 0 ? (metrics.passed / metrics.total) * 100 : 0);
    });

    // Exercise success rates
    const exerciseSuccessMap = new Map<string, { total: number; passed: number }>();
    exerciseEvents.forEach((event) => {
      const topicTitle = (event.properties.topic_title as string) || 'Unknown';
      const score = (event.properties.score as number) || 0;
      const passed = score >= 0.7;

      if (!exerciseSuccessMap.has(topicTitle)) {
        exerciseSuccessMap.set(topicTitle, { total: 0, passed: 0 });
      }
      const metrics = exerciseSuccessMap.get(topicTitle)!;
      metrics.total++;
      if (passed) metrics.passed++;
    });

    const exerciseSuccessRates = new Map<string, number>();
    exerciseSuccessMap.forEach((metrics, topic) => {
      exerciseSuccessRates.set(topic, metrics.total > 0 ? (metrics.passed / metrics.total) * 100 : 0);
    });

    // Stuck users (no progress in 7+ days)
    const userLastProgress = new Map<string, { topicTitle: string; lastProgress: number; lastUpdate: Date }>();
    const now = new Date();

    progressEvents.forEach((event) => {
      const userId = event.userId;
      const topicTitle = event.properties.topic_title as string;
      const completionPercentage = (event.properties.completion_percentage as number) || 0;
      const eventDate = event.timestamp instanceof Date ? event.timestamp : new Date(event.timestamp);

      if (topicTitle) {
        const key = `${userId}_${topicTitle}`;
        const existing = userLastProgress.get(key);
        
        if (!existing || eventDate > existing.lastUpdate) {
          userLastProgress.set(key, {
            topicTitle,
            lastProgress: completionPercentage,
            lastUpdate: eventDate,
          });
        }
      }
    });

    const stuckUsers = Array.from(userLastProgress.entries())
      .map(([key, data]) => {
        const daysSinceUpdate = differenceInDays(now, data.lastUpdate);
        return {
          userId: key.split('_')[0],
          topicTitle: data.topicTitle,
          lastProgress: data.lastProgress,
          daysSinceUpdate,
        };
      })
      .filter((user) => user.daysSinceUpdate >= 7)
      .sort((a, b) => b.daysSinceUpdate - a.daysSinceUpdate)
      .slice(0, 50);

    // Progress velocity (simplified)
    const progressVelocity: ProgressVelocity[] = [];

    // NEW: Get comprehensive learning path analytics from performance tracking
    // This would ideally come from a backend service that aggregates PerformanceTrackingService data
    // For now, we'll calculate from available analytics events
    const modulesPerformanceOverTime: PerformanceOverTime[] = [];
    const exercisesPerformanceOverTime: PerformanceOverTime[] = [];
    const quizzesPerformanceOverTime: PerformanceOverTime[] = [];
    const weakAreas: WeakArea[] = [];
    const practicePlanSuggestions: PracticePlanSuggestion[] = [];
    
    // Group performance by week for modules/exercises/quizzes
    const moduleEvents = events.filter((e) => e.eventName.includes('module'));
    const exerciseEventsByWeek = events.filter((e) => e.eventName.includes('exercise'));
    const quizEventsByWeek = events.filter((e) => e.eventName.includes('quiz'));
    
    // Calculate performance over time (grouped by week)
    const modulePerformanceByWeek = new Map<string, { total: number; passed: number; scores: number[] }>();
    const exercisePerformanceByWeek = new Map<string, { total: number; passed: number; scores: number[] }>();
    const quizPerformanceByWeek = new Map<string, { total: number; passed: number; scores: number[] }>();
    
    const getWeekKey = (date: Date): string => {
      const year = date.getFullYear();
      const week = Math.ceil((date.getTime() - new Date(year, 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
      return `${year}-W${week}`;
    };
    
    moduleEvents.forEach((event) => {
      const eventDate = event.timestamp instanceof Date ? event.timestamp : new Date(event.timestamp);
      const weekKey = getWeekKey(eventDate);
      const score = (event.properties?.score as number) || 0;
      const passed = score >= 0.7;
      
      if (!modulePerformanceByWeek.has(weekKey)) {
        modulePerformanceByWeek.set(weekKey, { total: 0, passed: 0, scores: [] });
      }
      const weekData = modulePerformanceByWeek.get(weekKey)!;
      weekData.total++;
      if (passed) weekData.passed++;
      weekData.scores.push(score);
    });
    
    exerciseEventsByWeek.forEach((event) => {
      const eventDate = event.timestamp instanceof Date ? event.timestamp : new Date(event.timestamp);
      const weekKey = getWeekKey(eventDate);
      const score = (event.properties?.score as number) || 0;
      const passed = score >= 0.7;
      
      if (!exercisePerformanceByWeek.has(weekKey)) {
        exercisePerformanceByWeek.set(weekKey, { total: 0, passed: 0, scores: [] });
      }
      const weekData = exercisePerformanceByWeek.get(weekKey)!;
      weekData.total++;
      if (passed) weekData.passed++;
      weekData.scores.push(score);
    });
    
    quizEventsByWeek.forEach((event) => {
      const eventDate = event.timestamp instanceof Date ? event.timestamp : new Date(event.timestamp);
      const weekKey = getWeekKey(eventDate);
      const score = (event.properties?.score as number) || 0;
      const passed = score >= 0.7;
      
      if (!quizPerformanceByWeek.has(weekKey)) {
        quizPerformanceByWeek.set(weekKey, { total: 0, passed: 0, scores: [] });
      }
      const weekData = quizPerformanceByWeek.get(weekKey)!;
      weekData.total++;
      if (passed) weekData.passed++;
      weekData.scores.push(score);
    });
    
    // Convert to PerformanceOverTime format
    modulePerformanceByWeek.forEach((data, week) => {
      const avgAccuracy = data.scores.length > 0
        ? data.scores.reduce((a, b) => a + b, 0) / data.scores.length
        : 0;
      modulesPerformanceOverTime.push({
        week,
        average_accuracy: avgAccuracy,
        total_items: data.total,
        completed_items: data.passed,
        completion_rate: data.total > 0 ? data.passed / data.total : 0,
      });
    });
    
    exercisePerformanceByWeek.forEach((data, week) => {
      const avgAccuracy = data.scores.length > 0
        ? data.scores.reduce((a, b) => a + b, 0) / data.scores.length
        : 0;
      exercisesPerformanceOverTime.push({
        week,
        average_accuracy: avgAccuracy,
        total_items: data.total,
        completed_items: data.passed,
        completion_rate: data.total > 0 ? data.passed / data.total : 0,
      });
    });
    
    quizPerformanceByWeek.forEach((data, week) => {
      const avgAccuracy = data.scores.length > 0
        ? data.scores.reduce((a, b) => a + b, 0) / data.scores.length
        : 0;
      quizzesPerformanceOverTime.push({
        week,
        average_accuracy: avgAccuracy,
        total_items: data.total,
        completed_items: data.passed,
        completion_rate: data.total > 0 ? data.passed / data.total : 0,
      });
    });
    
    // Identify weak areas (topics with low completion rates or low success rates)
    topicCompletionMetrics.forEach((topic) => {
      if (topic.completionRate < 50 || topic.averageTimeToComplete > 60) {
        weakAreas.push({
          topic: topic.topicTitle,
          modules_count: 0, // Would need module-level data
          exercises_count: 0, // Would need exercise-level data
          quizzes_count: 0, // Would need quiz-level data
          overall_accuracy: topic.completionRate / 100,
          needs_review: true,
        });
      }
    });
    
    // Generate practice plan suggestions for weak areas
    weakAreas.slice(0, 5).forEach((weakArea) => {
      practicePlanSuggestions.push({
        topic: weakArea.topic,
        priority: weakArea.overall_accuracy < 0.5 ? 'high' : 'medium',
        reason: `Low completion rate (${(weakArea.overall_accuracy * 100).toFixed(1)}%)`,
        recommended_actions: [
          'Review modules in this topic',
          'Practice exercises to reinforce concepts',
          'Retake quizzes to assess understanding',
        ],
        estimated_time_minutes: 30,
      });
    });
    
    // Calculate overall metrics
    const allScores = [...moduleEvents, ...exerciseEvents, ...quizEvents]
      .map((e) => (e.properties?.score as number) || 0)
      .filter((s) => s > 0);
    const overallAccuracy = allScores.length > 0
      ? allScores.reduce((a, b) => a + b, 0) / allScores.length
      : 0;
    const completionRate = topicCompletionMetrics.length > 0
      ? topicCompletionMetrics.reduce((sum, t) => sum + t.completionRate, 0) / topicCompletionMetrics.length / 100
      : 0;

    return {
      topicCompletionMetrics: topicCompletionMetrics.slice(0, 50),
      dropOffPoints,
      stuckUsers,
      quizSuccessRates,
      exerciseSuccessRates,
      progressVelocity,
      // NEW: Comprehensive learning path analytics
      modulesPerformanceOverTime: modulesPerformanceOverTime.sort((a, b) => a.week.localeCompare(b.week)),
      exercisesPerformanceOverTime: exercisesPerformanceOverTime.sort((a, b) => a.week.localeCompare(b.week)),
      quizzesPerformanceOverTime: quizzesPerformanceOverTime.sort((a, b) => a.week.localeCompare(b.week)),
      weakAreas: weakAreas.slice(0, 10),
      practicePlanSuggestions: practicePlanSuggestions,
      overallAccuracy,
      completionRate,
    };
  } catch (error) {
    console.error('Error calculating learning progress analytics:', error);
    throw error;
  }
};

